import React, { useState, useRef, useEffect } from 'react'
import styled, { keyframes, css } from 'styled-components'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(5px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
`

const Container = styled.div`
    position: relative;
    display: inline-block;
`

const TriggerBadge = styled.div`
    background: ${({ isOpen, isHighlighted }) =>
        isOpen || isHighlighted ? 'rgba(99, 102, 241, 0.15)' : 'rgba(0, 0, 0, 0.6)'};
    border: 1px solid ${({ isOpen, isHighlighted }) =>
        (isOpen || isHighlighted) ? '#6366f1' : '#6464939e'};
    color: ${({ isOpen, isHighlighted }) => (isOpen || isHighlighted) ? '#fff' : 'rgba(255, 255, 255, 0.75)'};
    font-family: var(--font-roboto), monospace;
    padding: 5px 4px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 400;
    min-width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;
    box-shadow: ${({ isOpen, isHighlighted }) =>
        (isOpen || isHighlighted) ? '0 0 10px rgba(99, 102, 241, 0.3)' : 'none'};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    &:hover {
        border-color: ${({ disabled }) => (disabled ? 'rgba(255, 255, 255, 0.05)' : '#6366f1')};
        color: ${({ disabled }) => (disabled ? 'rgba(255, 255, 255, 0.5)' : '#fff')};
        background: ${({ disabled }) => (disabled ? 'transparent' : 'rgba(99, 102, 241, 0.15)')};
    }
`

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.4); 
    //backdrop-filter: blur(1px); 
    z-index: 100;
    animation: ${fadeIn} 0.2s ease-out;
`

const Popover = styled.div`
    position: absolute;
    right: 0;
    width: 200px;
    background: rgba(15, 23, 42, 0.9);
    backdrop-filter: blur(20px);
    border: 2px solid rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05);
    z-index: 300; 
    animation: ${fadeIn} 0.15s cubic-bezier(0.16, 1, 0.3, 1);
    
    display: flex;
    flex-direction: column;
    gap: 8px;

    ${({ showAbove }) => showAbove ? css`
        bottom: calc(100% + 8px);
        transform-origin: bottom right;
    ` : css`
        top: calc(100% + 8px);
        transform-origin: top right;
    `}

    &::after {
        content: '';
        position: absolute;
        right: 18px; 
        width: 10px;
        height: 10px;
        background: rgba(15, 23, 42, 0.9);
        transform: rotate(45deg);
        border: 2px solid rgba(255, 255, 255, 0.05);
        box-sizing: border-box;

        ${({ showAbove }) => showAbove ? css`
            bottom: -6.5px;
            border-top: none;
            border-left: none;
        ` : css`
            top: -6.5px;
            border-bottom: none;
            border-right: none;
        `}
    }
`

const PopoverTitle = styled.div`
    position: absolute;
    left: 0;
    /* transform removed for left alignment */
    
    background: #050208;
    border: 1px solid rgba(255, 255, 255, 0.05);
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);

    color: #fff;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    pointer-events: none;
    white-space: nowrap;
    
    ${({ showAbove }) => showAbove ? css`
        top: -34px;
    ` : css`
        bottom: -34px;
    `}
`

const InputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    margin-bottom: 8px;
`

const StyledInput = styled.input`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    color: #fff;
    font-family: var(--font-roboto);
    font-size: 1rem;
    font-weight: 400;
    padding: 4px 0;
    text-align: center;
    width: 100%;
    outline: none;
    transition: all 0.2s;
    height: 30px;

    &:focus {
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }

    &::-webkit-inner-spin-button, 
    &::-webkit-outer-spin-button { 
        -webkit-appearance: none; 
        margin: 0; 
    }
`

const ActionButton = styled.button`
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    border: 1px solid transparent;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s;
    flex-shrink: 0;

    &:hover {
        background: rgba(99, 102, 241, 0.15);
        border-color: #6366f1;
    }

    svg {
        font-size: 16px;
    }
`

const PresetsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 10px;
    width: 100%;
`

const PresetButton = styled.button`
    min-width: 28px;
    background: ${({ isActive }) => isActive ? '#6366f1' : 'rgba(255, 255, 255, 0.05)'};
    color: ${({ isActive }) => isActive ? '#fff' : 'rgba(255, 255, 255, 0.5)'};
    padding: 4px;
    border-radius: 4px;
    border: 1px solid ${({ isActive }) => isActive ? '#6366f1' : 'transparent'};
    font-size: 0.7rem;
    font-family: var(--font-roboto), monospace;
    font-weight: 400;
    cursor: pointer;
    transition: all 0.2s;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    &:hover {
        background: ${({ isActive }) => isActive ? '#6366f1' : 'rgba(255, 255, 255, 0.1)'};
        color: white;
        border-color: ${({ isActive }) => isActive ? '#6366f1' : 'rgba(255, 255, 255, 0.2)'};
    }
`

const PopoverSlider = styled.input`
    -webkit-appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.1);
    outline: none;
    transition: background 0.2s;
    cursor: pointer;
    margin: 4px 0 0 0;

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #6366f1;
        cursor: pointer;
        transition: transform 0.1s, background-color 0.2s;
        box-shadow: 0 0 0 2px #050208;
        margin-top: -2px;

        &:hover {
            transform: scale(1.1);
            filter: brightness(1.2);
        }
    }
`

export default function SmartValueInput({
    value,
    onChange,
    min,
    max,
    step = 1,
    presets = [],
    unit = '',
    showSlider = false,
    label,
    disabled = false,
    isHighlighted = false
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState(value)
    const [showAbove, setShowAbove] = useState(true)
    const containerRef = useRef(null)
    const inputRef = useRef(null)

    useEffect(() => {
        setInputValue(value)
    }, [value])

    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            if (rect.top < 200) {
                setShowAbove(false)
            } else {
                setShowAbove(true)
            }

            if (inputRef.current) {
                setTimeout(() => {
                    inputRef.current?.focus({ preventScroll: true })
                }, 50)
            }
        }
    }, [isOpen])

    const handleInputChange = (e) => {
        setInputValue(e.target.value)
    }

    const handleBlur = () => {
        let newVal = parseFloat(inputValue)
        if (isNaN(newVal)) newVal = min || 0
        if (min !== undefined && newVal < min) newVal = min
        if (max !== undefined && newVal > max) newVal = max

        if (step) {
            const precision = (step.toString().split('.')[1] || '').length
            newVal = parseFloat(newVal.toFixed(precision))
        }

        onChange(newVal)
        setInputValue(newVal)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleBlur()
            setIsOpen(false)
        }
    }

    const increment = () => {
        const current = parseFloat(inputValue) || 0
        const newVal = Math.min(max !== undefined ? max : Infinity, current + step)
        const precision = (step.toString().split('.')[1] || '').length
        const rounded = parseFloat(newVal.toFixed(precision))

        onChange(rounded)
        setInputValue(rounded)
        inputRef.current?.focus({ preventScroll: true })
    }

    const decrement = () => {
        const current = parseFloat(inputValue) || 0
        const newVal = Math.max(min !== undefined ? min : -Infinity, current - step)
        const precision = (step.toString().split('.')[1] || '').length
        const rounded = parseFloat(newVal.toFixed(precision))

        onChange(rounded)
        setInputValue(rounded)
        inputRef.current?.focus({ preventScroll: true })
    }

    const handlePresetClick = (val) => {
        onChange(val)
        setInputValue(val)
    }

    const handleSliderChange = (e) => {
        const val = parseFloat(e.target.value)
        onChange(val)
        setInputValue(val)
    }

    return (
        <Container ref={containerRef}>
            <TriggerBadge
                isOpen={isOpen}
                isHighlighted={isHighlighted}
                disabled={disabled}
                onClick={(e) => {
                    if (disabled) return
                    e.stopPropagation()
                    e.preventDefault()
                    setIsOpen(!isOpen)
                }}
            >
                {value}{unit}
            </TriggerBadge>

            {isOpen && !disabled && (
                <>
                    <Backdrop
                        onClick={(e) => {
                            e.stopPropagation()
                            setIsOpen(false)
                            handleBlur()
                        }}
                    />
                    <Popover showAbove={showAbove} onClick={(e) => e.stopPropagation()}>
                        {label && (
                            <PopoverTitle showAbove={showAbove}>
                                {label}
                            </PopoverTitle>
                        )}

                        <InputRow>
                            <ActionButton onClick={decrement}><RemoveIcon /></ActionButton>
                            <StyledInput
                                ref={inputRef}
                                type="number"
                                value={inputValue}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                step={step}
                            />
                            <ActionButton onClick={increment}><AddIcon /></ActionButton>
                        </InputRow>

                        {showSlider && (
                            <PopoverSlider
                                type="range"
                                min={min}
                                max={max}
                                step={step}
                                value={typeof value === 'number' ? value : min}
                                onChange={handleSliderChange}
                            />
                        )}

                        {presets.length > 0 && (
                            <PresetsGrid>
                                {presets.map((preset, idx) => {
                                    const pVal = typeof preset === 'object' ? preset.val : preset
                                    const pLabel = typeof preset === 'object' ? preset.label : preset
                                    return (
                                        <PresetButton
                                            key={idx}
                                            isActive={value === pVal}
                                            onClick={() => handlePresetClick(pVal)}
                                        >
                                            {pLabel}
                                        </PresetButton>
                                    )
                                })}
                            </PresetsGrid>
                        )}
                    </Popover>
                </>
            )}
        </Container>
    )
}
